import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  GitBranch, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Tags,
  BarChart3,
  Zap
} from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { CourseTypeService } from '@/services/courseTypeService';
import { useToast } from '@/hooks/use-toast';

export const CourseIntegrationDashboard: React.FC = () => {
  const { userRole } = useSupabaseAuth();
  const { toast } = useToast();
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const canManageIntegration = userRole === 'admin' || userRole === 'staff';

  useEffect(() => {
    if (canManageIntegration) {
      runIntegrationAnalysis();
    }
  }, [canManageIntegration]);

  const runIntegrationAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      // Mock analysis data - would analyze actual courses
      const mockResults = {
        typeAnalysis: {
          totalCourses: 45,
          needsStandardization: 12,
          typeDistribution: {
            'react': 8,
            'javascript': 7,
            'python': 6,
            'sql': 5,
            'ai': 4,
            'general': 12,
            'other': 3
          },
          inconsistencies: [
            { courseId: 'course-1', title: 'React Basics', detectedType: 'react', currentType: 'frontend', confidence: 85 },
            { courseId: 'course-2', title: 'JS Advanced', detectedType: 'javascript', currentType: 'programming', confidence: 92 },
            { courseId: 'course-3', title: 'Data Analysis', detectedType: 'python', currentType: 'datascience', confidence: 78 }
          ]
        },
        navigationAnalysis: {
          coursesWithNavigation: 38,
          coursesNeedingNavigation: 7,
          averageModulesPerCourse: 12.5,
          coursesWithLockedModules: 28,
          issuesFound: [
            'Inconsistent module unlocking logic in 5 courses',
            'Missing prerequisites in 3 courses',
            'Navigation rules not defined in 7 courses'
          ]
        },
        progressTracking: {
          coursesWithTracking: 40,
          coursesNeedingEnhancement: 5,
          averageCompletionRate: 73.2,
          featuresNeeded: [
            'Enhanced progress visualization',
            'Milestone tracking',
            'Achievement system integration'
          ]
        }
      };

      setAnalysisResults(mockResults);
    } catch (error) {
      console.error('Integration analysis failed:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze course integration",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleStandardizeTypes = async () => {
    try {
      // Mock standardization process
      toast({
        title: "Type Standardization Started",
        description: "Standardizing course types across the platform",
      });

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      toast({
        title: "Types Standardized",
        description: "Successfully standardized 12 course types",
      });

      // Refresh analysis
      runIntegrationAnalysis();
    } catch (error) {
      toast({
        title: "Standardization Failed",
        description: "Failed to standardize course types",
        variant: "destructive",
      });
    }
  };

  const handleFixNavigation = async () => {
    try {
      toast({
        title: "Navigation Enhancement Started",
        description: "Updating course navigation systems",
      });

      await new Promise(resolve => setTimeout(resolve, 1500));

      toast({
        title: "Navigation Enhanced",
        description: "Successfully updated navigation for 7 courses",
      });

      runIntegrationAnalysis();
    } catch (error) {
      toast({
        title: "Navigation Update Failed",
        description: "Failed to enhance course navigation",
        variant: "destructive",
      });
    }
  };

  if (!canManageIntegration) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Course integration management requires admin or staff permissions.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isAnalyzing && !analysisResults) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Analyzing course integration...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Course Integration Dashboard</h2>
          <p className="text-muted-foreground">
            Manage course types, navigation, and integration consistency
          </p>
        </div>
        <Button 
          onClick={runIntegrationAnalysis}
          disabled={isAnalyzing}
          variant="outline"
        >
          {isAnalyzing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
          Refresh Analysis
        </Button>
      </div>

      {analysisResults && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Course Types</CardTitle>
                <Tags className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analysisResults.typeAnalysis.needsStandardization}
                </div>
                <p className="text-xs text-muted-foreground">
                  courses need type standardization
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Navigation</CardTitle>
                <GitBranch className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analysisResults.navigationAnalysis.coursesNeedingNavigation}
                </div>
                <p className="text-xs text-muted-foreground">
                  courses need navigation enhancement
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Progress Tracking</CardTitle>
                <BarChart3 className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analysisResults.progressTracking.averageCompletionRate}%
                </div>
                <p className="text-xs text-muted-foreground">
                  average completion rate
                </p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="types" className="w-full">
            <TabsList>
              <TabsTrigger value="types">Course Types</TabsTrigger>
              <TabsTrigger value="navigation">Navigation</TabsTrigger>
              <TabsTrigger value="progress">Progress Tracking</TabsTrigger>
            </TabsList>

            <TabsContent value="types" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Type Distribution</CardTitle>
                    <CardDescription>
                      Current distribution of course types
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(analysisResults.typeAnalysis.typeDistribution).map(([type, count]) => (
                        <div key={type} className="flex justify-between items-center">
                          <span className="capitalize">{type}</span>
                          <Badge variant="outline">{String(count)}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Type Inconsistencies</CardTitle>
                    <CardDescription>
                      Courses with type classification issues
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analysisResults.typeAnalysis.inconsistencies.length > 0 ? (
                      <div className="space-y-2">
                        {analysisResults.typeAnalysis.inconsistencies.map((issue: any, index: number) => (
                          <div key={index} className="p-2 border rounded text-sm">
                            <div className="font-medium">{issue.title}</div>
                            <div className="text-muted-foreground">
                              Detected: {issue.detectedType} | Current: {issue.currentType} | 
                              Confidence: {issue.confidence}%
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-green-600">No type inconsistencies found!</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {analysisResults.typeAnalysis.needsStandardization > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {analysisResults.typeAnalysis.needsStandardization} courses need type standardization.
                    <Button 
                      size="sm" 
                      className="ml-2"
                      onClick={handleStandardizeTypes}
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      Standardize Types
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="navigation" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Navigation Analysis</CardTitle>
                  <CardDescription>
                    Course navigation system health
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium mb-2">Navigation Status</div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm">With Navigation</span>
                          <span className="text-sm font-medium">
                            {analysisResults.navigationAnalysis.coursesWithNavigation}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Need Enhancement</span>
                          <span className="text-sm font-medium text-orange-600">
                            {analysisResults.navigationAnalysis.coursesNeedingNavigation}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Avg Modules</span>
                          <span className="text-sm font-medium">
                            {analysisResults.navigationAnalysis.averageModulesPerCourse}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium mb-2">Issues Found</div>
                      <div className="space-y-1">
                        {analysisResults.navigationAnalysis.issuesFound.map((issue: string, index: number) => (
                          <div key={index} className="text-sm text-muted-foreground">
                            • {issue}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {analysisResults.navigationAnalysis.coursesNeedingNavigation > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {analysisResults.navigationAnalysis.coursesNeedingNavigation} courses need navigation enhancement.
                    <Button 
                      size="sm" 
                      className="ml-2"
                      onClick={handleFixNavigation}
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      Enhance Navigation
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="progress" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Progress Tracking Status</CardTitle>
                  <CardDescription>
                    Progress tracking system analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium mb-2">Tracking Status</div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm">With Tracking</span>
                          <span className="text-sm font-medium text-green-600">
                            {analysisResults.progressTracking.coursesWithTracking}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Need Enhancement</span>
                          <span className="text-sm font-medium text-orange-600">
                            {analysisResults.progressTracking.coursesNeedingEnhancement}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Completion Rate</span>
                          <span className="text-sm font-medium">
                            {analysisResults.progressTracking.averageCompletionRate}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium mb-2">Enhancement Opportunities</div>
                      <div className="space-y-1">
                        {analysisResults.progressTracking.featuresNeeded.map((feature: string, index: number) => (
                          <div key={index} className="text-sm text-muted-foreground">
                            • {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {analysisResults.progressTracking.averageCompletionRate < 80 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Average completion rate is below 80%. Consider implementing enhanced progress tracking features.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};